
import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { SeoRouteConfig, ValidatedWhitelabelConfig } from '../config/whitelabel-config.model';
import { findRouteLandingByPath } from './route-landing';

@Injectable({ providedIn: 'root' })
export class SeoService {
  private activeConfig: ValidatedWhitelabelConfig | null = null;
  private isWatchingRoutes = false;

  constructor(
    private title: Title,
    private meta: Meta,
    private router: Router,
    @Inject(DOCUMENT) private document: Document
  ) {}

  apply(config: ValidatedWhitelabelConfig, routeKey = 'default', language = 'en'): void {
    this.activeConfig = config;
    const routePath = this.pathFromUrl(this.router.url);
    const resolvedRouteKey = routeKey === 'default' ? this.routeKey(routePath) : routeKey;
    this.applyForRoute(config, resolvedRouteKey, language, routePath);
    this.injectOrganizationSchema(config);

    if (!this.isWatchingRoutes) {
      this.isWatchingRoutes = true;
      this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe(event => {
          if (!this.activeConfig) return;
          const path = this.pathFromUrl(event.urlAfterRedirects);
          this.applyForRoute(this.activeConfig, this.routeKey(path), language, path);
        });
    }
  }

  private applyForRoute(
    config: ValidatedWhitelabelConfig,
    routeKey: string,
    language: string,
    path: string
  ): void {
    const routeLanding = findRouteLandingByPath(config.slides, path);
    const route = config.seo[language] && (
      config.seo[language][routeKey] || config.seo[language].default
    );
    if (routeLanding) {
      this.applyRoute({
        title: `${routeLanding.origin} to ${routeLanding.destination} Bus Tickets | ${config.site.name}`,
        description: `Book ${routeLanding.origin} to ${routeLanding.destination} bus tickets with ${config.site.name}. Search schedules, fares and live seat availability, then reserve your seat online.`,
        keywords: `${routeLanding.origin} to ${routeLanding.destination} bus, ${routeLanding.origin} to ${routeLanding.destination} tickets, ${config.site.name}`
      }, config);
    } else if (route) {
      this.applyRoute(route, config);
    } else {
      this.title.setTitle(config.site.name);
    }

    this.meta.updateTag({
      name: 'robots',
      content: config.site.indexed && this.isIndexablePath(config, path)
        ? 'index,follow'
        : 'noindex,nofollow'
    });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:site_name', content: config.site.name });
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    const canonicalUrl = this.canonicalForPath(config.site.canonicalUrl, path);
    if (canonicalUrl) {
      this.meta.updateTag({ property: 'og:url', content: canonicalUrl });
    }
    const socialImage = routeLanding?.image || config.self.logo;
    if (socialImage) {
      const imageUrl = this.absoluteUrl(socialImage, config.site.canonicalUrl);
      this.meta.updateTag({
        property: 'og:image',
        content: imageUrl
      });
      this.meta.updateTag({ name: 'twitter:image', content: imageUrl });
    }
    this.setCanonical(canonicalUrl);
    this.setRouteSchema(routeLanding, config, canonicalUrl);
    this.setFaqSchema(config, path);
  }

  private applyRoute(route: SeoRouteConfig, config: ValidatedWhitelabelConfig): void {
    const interpolate = (value: string) => value
      .replace(/\{appName\}/g, config.routeConfig.appName || config.site.name)
      .replace(/\{countryName\}/g, config.site.countryName);
    this.title.setTitle(interpolate(route.title));
    const description = interpolate(route.description);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ property: 'og:title', content: interpolate(route.title) });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ name: 'twitter:title', content: interpolate(route.title) });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    if (route.keywords) {
      this.meta.updateTag({ name: 'keywords', content: interpolate(route.keywords) });
    }
  }

  private setCanonical(url: string): void {
    const existing = this.document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!url) {
      existing && existing.remove();
      return;
    }
    const canonical = existing || this.document.createElement('link');
    canonical.rel = 'canonical';
    canonical.href = url;
    if (!existing) this.document.head.appendChild(canonical);
  }

  private injectOrganizationSchema(config: ValidatedWhitelabelConfig): void {
    const sameAs = Array.from(new Set([
      config.site.marketplaceUrl,
      config.site.appStoreUrl,
      config.site.iosAppStoreUrl,
      ...Object.values(config.socialLinks)
    ].filter((value): value is string => !!value)));
    const alternateNames = (config.site.alternateNames || []).filter(Boolean);
    const rootUrl = this.canonicalForPath(config.site.canonicalUrl, '/');
    const logo = this.absoluteUrl(config.self.logo, rootUrl);

    this.setJsonLd('runtime-organization-schema', {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      '@id': `${rootUrl}#organization`,
      name: config.site.name,
      ...(config.site.legalName ? { legalName: config.site.legalName } : {}),
      ...(alternateNames.length ? { alternateName: alternateNames } : {}),
      url: rootUrl,
      ...(logo ? { logo } : {}),
      ...(config.site.operatorAbout ? { description: config.site.operatorAbout } : {}),
      ...(config.site.supportEmail ? { email: config.site.supportEmail } : {}),
      ...(config.site.supportNumber ? { telephone: config.site.supportNumber } : {}),
      ...(config.site.supportNumber || config.site.supportEmail ? {
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer service',
          ...(config.site.supportNumber ? { telephone: config.site.supportNumber } : {}),
          ...(config.site.supportEmail ? { email: config.site.supportEmail } : {})
        }
      } : {}),
      ...(config.site.address ? {
        address: {
          '@type': 'PostalAddress',
          streetAddress: config.site.address,
          addressCountry: config.site.countryCode
        }
      } : {}),
      ...(sameAs.length ? { sameAs } : {})
    });

    this.setJsonLd('runtime-website-schema', {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': `${rootUrl}#website`,
      url: rootUrl,
      name: config.site.name,
      ...(alternateNames.length ? { alternateName: alternateNames } : {}),
      publisher: { '@id': `${rootUrl}#organization` }
    });
  }

  private setFaqSchema(config: ValidatedWhitelabelConfig, path: string): void {
    const id = 'runtime-faq-schema';
    const faq = this.contactFaq(config);
    if (path !== '/contact-us' || !faq.length) {
      this.document.getElementById(id)?.remove();
      return;
    }

    this.setJsonLd(id, {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faq.map(item => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer
        }
      }))
    });
  }

  private contactFaq(config: ValidatedWhitelabelConfig): Array<{ question: string; answer: string }> {
    const contact = config.content?.['contact'];
    if (!contact || typeof contact !== 'object' || Array.isArray(contact)) return [];
    const faq = (contact as Record<string, unknown>)['faq'];
    if (!Array.isArray(faq)) return [];

    return faq.filter((item): item is { question: string; answer: string } =>
      !!item &&
      typeof item === 'object' &&
      typeof (item as Record<string, unknown>)['question'] === 'string' &&
      typeof (item as Record<string, unknown>)['answer'] === 'string'
    );
  }

  private setJsonLd(id: string, value: object): void {
    let script = this.document.getElementById(id) as HTMLScriptElement | null;
    if (!script) {
      script = this.document.createElement('script');
      script.id = id;
      script.type = 'application/ld+json';
      this.document.head.appendChild(script);
    }
    script.text = JSON.stringify(value);
  }

  private setRouteSchema(
    route: ReturnType<typeof findRouteLandingByPath>,
    config: ValidatedWhitelabelConfig,
    canonicalUrl: string
  ): void {
    const id = 'runtime-route-schema';
    if (!route) {
      this.document.getElementById(id)?.remove();
      return;
    }

    this.setJsonLd(id, {
      '@context': 'https://schema.org',
      '@type': 'BusTrip',
      '@id': `${canonicalUrl}#trip`,
      name: `${route.origin} to ${route.destination} bus`,
      url: canonicalUrl,
      provider: { '@id': `${this.canonicalForPath(config.site.canonicalUrl, '/')}#organization` },
      departureBusStop: { '@type': 'BusStop', name: route.origin },
      arrivalBusStop: { '@type': 'BusStop', name: route.destination }
    });
  }

  private routeKey(path: string): string {
    const routeKeys: Record<string, string> = {
      '/': 'default',
      '/home': 'default',
      '/about-us': 'about',
      '/contact-us': 'contact',
      '/parcel': 'parcel',
      '/privacy-policy': 'privacy',
      '/terms-and-condition': 'terms'
    };
    return routeKeys[path] || 'default';
  }

  private isIndexablePath(config: ValidatedWhitelabelConfig, path: string): boolean {
    const isPublicPage = [
      '/',
      '/home',
      '/about-us',
      '/contact-us',
      '/parcel',
      '/privacy-policy',
      '/terms-and-condition'
    ].includes(path);
    return isPublicPage || !!findRouteLandingByPath(config.slides, path);
  }

  private pathFromUrl(url: string): string {
    const path = (url || '/').split(/[?#]/, 1)[0] || '/';
    return path.length > 1 ? path.replace(/\/+$/, '') : path;
  }

  private canonicalForPath(baseUrl: string, path: string): string {
    if (!baseUrl) return '';
    try {
      const url = new URL(baseUrl);
      url.pathname = path === '/home' ? '/' : path;
      url.search = '';
      url.hash = '';
      return url.toString();
    } catch {
      return '';
    }
  }

  private absoluteUrl(value: string, baseUrl: string): string {
    if (!value) return '';
    try {
      return new URL(value, baseUrl).toString();
    } catch {
      return value;
    }
  }
}
